import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Forgets this browser's session without the auth server's help.
 *
 * WHY. signOut({ scope: 'local' }) tells the auth server first and, when that request fails
 * with anything but 401/403/404 (offline, a network blip, a 5xx), returns the error and KEEPS
 * the stored session (auth-js 2.110 GoTrueClient._signOut). The CRM went to /login all the
 * same, so the user believed they were out while the CRM was still signed in: reopening it,
 * or the next person at a shared clinic computer opening it, was in as them. Seen on local
 * 2026-11-05 with the logout request blocked.
 *
 * HOW. auth-js's own _removeSession(), the step a successful sign-out ends with: it removes
 * the stored keys and emits SIGNED_OUT, which also reaches the CRM's other open tabs. It is
 * internal, so if a later auth-js drops it, the stored keys are removed directly (other tabs
 * then only notice on their next load). The session itself stays valid on the server until it
 * expires; nothing in this browser can use it any more.
 */
export async function forgetLocalSession(client: SupabaseClient): Promise<void> {
  const auth = client.auth as unknown as {
    _removeSession?: () => Promise<void>;
    storage?: { removeItem(key: string): unknown };
    storageKey?: string;
  };
  if (typeof auth._removeSession === 'function') {
    await auth._removeSession();
    return;
  }
  if (auth.storage && auth.storageKey) {
    for (const suffix of ['', '-code-verifier', '-user']) {
      await auth.storage.removeItem(auth.storageKey + suffix);
    }
  }
}
